import { formatRupeeLabel } from '../../../../display-values/lumpsumPlanAmount'
import { useEffect, useRef, useState } from 'react'
import * as d3 from 'd3'
import { rounded_rect } from '../../../../../../../utils/graphUtils'
type MissedGainsComparisonBarGraphProps = {
  regularValue: number
  directValue: number
  currentBarLabel?: string | null
  switchBarLabel?: string | null
}

export function MissedGainsComparisonBarGraph({
  regularValue,
  directValue,
  currentBarLabel,
  switchBarLabel
}: MissedGainsComparisonBarGraphProps) {
  const svgRef = useRef<SVGSVGElement | null>(null)
  const [dimensions, setDimensions] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0
  })

  useEffect(() => {
    const resizeListener = () => {
      if (svgRef.current) {
        const { width, height } = svgRef.current.getBoundingClientRect()
        setDimensions({ width, height })
      }
    }
    window.addEventListener('resize', resizeListener)
    resizeListener()
    return () => window.removeEventListener('resize', resizeListener)
  }, [])

  useEffect(() => {
    if (!svgRef.current || dimensions.width === 0 || dimensions.height === 0) return

    let topTextHeight = 0
    let bottomTextHeight = 0

    let domainMin = Math.min(0, regularValue, directValue)
    let domainMax = Math.max(0, regularValue, directValue)

    const maxValue = Math.max(regularValue, directValue)
    const minValue = Math.min(regularValue, directValue)

    let origin = 0

    if (minValue > 0 && maxValue > 0) {
      domainMin = minValue * 0.8
      domainMax = maxValue
      origin = domainMin
      topTextHeight = 25
      bottomTextHeight = 55
    } else if (minValue < 0 && maxValue < 0) {
      domainMin = minValue
      domainMax = maxValue * 0.8
      origin = domainMax
      topTextHeight = 0
      bottomTextHeight = 85
    } else {
      domainMin = minValue * 1.2
      domainMax = maxValue
      origin = 0
      topTextHeight = 25
      bottomTextHeight = 80
    }

    const margin = { top: topTextHeight, right: 20, bottom: bottomTextHeight, left: 20 }
    const width = dimensions.width - margin.left - margin.right
    const height = dimensions.height - margin.top - margin.bottom

    const svg = d3
      .select(svgRef.current)
      .attr('width', '100%')
      .attr('height', '100%')
      .attr('viewBox', `0 0 ${dimensions.width} ${dimensions.height}`)

    svg.selectAll('*').remove()

    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`)

    const barWidth = 60
    const gapBetweenBars = 60
    const totalWidth = 2 * barWidth + gapBetweenBars
    const startX = Math.max(0, (width - totalWidth) / 2)

    const y = d3.scaleLinear().domain([domainMin, domainMax]).range([height, 0])
    const y0 = y(origin)

    const drawBar = (value: number, x: number, color: string) => {
      let barHeight: number
      let yPos: number
      let roundTop: boolean
      let roundBottom: boolean
      let labelY: number

      if (value >= 0) {
        yPos = y(value)
        barHeight = y0 - yPos
        roundTop = true
        roundBottom = false
        labelY = yPos - 10
      } else {
        yPos = y0
        barHeight = y(value) - y0
        roundTop = false
        roundBottom = true
        labelY = y(value) + 20
      }

      if (value !== 0 && barHeight < 2) {
        barHeight = 2
        if (value > 0) {
          yPos = y0 - barHeight
        }
      }

      g.append('path')
        .attr('d', rounded_rect(x, yPos, barWidth, barHeight, 4, roundTop, roundTop, roundBottom, roundBottom))
        .attr('fill', color)

      g.append('text')
        .attr('x', x + barWidth / 2)
        .attr('y', labelY)
        .attr('text-anchor', 'middle')
        .attr('font-size', '12px')
        .attr('font-weight', '500')
        .attr('fill', color)
        .text(`${value < 0 ? '-' : ''} ${formatRupeeLabel({ rawAmount: value })}`)
    }

    drawBar(regularValue, startX, '#2E8AE5')
    drawBar(directValue, startX + barWidth + gapBetweenBars, '#095EB2')

    g.append('line')
      .attr('x1', 0)
      .attr('y1', y0)
      .attr('x2', width)
      .attr('y2', y0)
      .attr('stroke', '#095EB2')
      .attr('stroke-width', 0.5)
      .attr('opacity', 0.3)

    const addCategoryLabel = (x: number, color: string, textLines: string[]) => {
      const yPos = regularValue < 0 || directValue < 0 ? y(domainMin) + 45 : y0 + 25
      const text = g
        .append('text')
        .attr('x', x)
        .attr('y', yPos)
        .attr('text-anchor', 'middle')
        .attr('font-size', '12px')
        .attr('font-weight', '400')
        .attr('fill', color)

      textLines.forEach((line, lineIndex) => {
        text
          .append('tspan')
          .attr('x', x)
          .attr('dy', lineIndex === 0 ? '0' : '1.2em')
          .text(line)
      })
    }

    const useCustomBarLabels =
      (currentBarLabel?.trim().length ?? 0) > 0 && (switchBarLabel?.trim().length ?? 0) > 0

    if (useCustomBarLabels) {
      addCategoryLabel(startX + barWidth / 2, '#2E8AE5', [currentBarLabel ?? ''])
      addCategoryLabel(startX + barWidth + gapBetweenBars + barWidth / 2, '#095EB2', [
        switchBarLabel ?? ''
      ])
    } else {
      addCategoryLabel(startX + barWidth / 2, '#2E8AE5', ['Your current', 'funds'])
      addCategoryLabel(startX + barWidth + gapBetweenBars + barWidth / 2, '#095EB2', [
        'Top-ranked',
        'funds'
      ])
    }
  }, [dimensions, regularValue, directValue, currentBarLabel, switchBarLabel])

  return <svg ref={svgRef} style={{ width: '100%', height: '100%' }} />
}

export function isMissedGainsComparisonAvailable({
  missedGains,
  baselineValue
}: {
  missedGains?: number | null
  baselineValue?: number | null
}): boolean {
  const safeMissedGains = missedGains ?? 0
  const safeBaselineValue = baselineValue ?? 0

  if (!Number.isFinite(safeMissedGains) || !Number.isFinite(safeBaselineValue)) {
    return false
  }

  return safeMissedGains > 0 && safeBaselineValue > 0
}

export function MissedGainsInfoUnavailableState() {
  return (
    <div className="flex min-h-[200px] items-center justify-center rounded-[12px] border border-[#F0F4F7] px-5 py-10">
      <p className="text-center text-[14px] font-[400] leading-[150%] text-[#6A6E6E]">
        Data unavailable
      </p>
    </div>
  )
}
